import { useEffect, useState } from "react"
import { graphql, useStaticQuery } from "gatsby"

import { useCore } from "@app/hooks/useCore"
import { useShopify } from "@app/hooks/useShopify"
import { useConfigContext } from "@app/providers/config"

import type { Location } from "@root/types/global"

export const useGiftcard = (location: Location) => {
  const {
    settings: { params },
  } = useConfigContext()
  const {
    helpers: { decodeBase64, getUrlParameter },
  } = useCore()
  const { formatMoney } = useShopify()
  const [card, setCard] = useState(null)
  const [errors, setErrors] = useState<Array<string>>([])
  const [show, setShow] = useState(false)

  const { giftcardSettings } = useStaticQuery<GatsbyTypes.StaticGiftcardPageQuery>(graphql`
    query StaticGiftcardPage {
      giftcardSettings: sanityPageGiftcard {
        balanceInputLabel
        balancePinLabel
        balanceSubmitLabel
        balanceTitle
        balanceSubTitle
        cancelLabel
        designHoverLabel
        drawerTitle
        formTitle
        giftcardDescription
        giftcardTitle
        messageLabel
        recipientEmailLabel
        recipientNameLabel
        senderLabel
        step1
        step2
        submitLabel
        title
        categories {
          category
          options
        }
        gcFaq {
          referenceTitle
          style
          accordions {
            title
            accordionItems {
              ... on SanityAccordionItem {
                _type
                _key
                question
                answer: _rawAnswer(resolveReferences: { maxDepth: 2 })
              }
            }
          }
        }
      }
    }
  `)

  const getCard = () => {
    const rawGiftcard = getUrlParameter(params?.giftcard, location)

    try {
      const card = rawGiftcard && JSON.parse(decodeBase64(rawGiftcard))

      if (card?.code)
        setCard({
          ...card,
          balance: {
            initial: formatMoney(card?.balance?.initial),
            remaining: formatMoney(card?.balance?.remaining),
          },
          code: {
            partial: card?.code?.slice(-4)?.padStart(card?.code?.length, "*"),
            full: card?.code,
          },
          enabled: card?.enabled && !card?.expired,
          expires: card?.expires && new Date(card?.expires),
        })
    } catch (error) {
      setErrors([(error as Error)?.message])
    }
  }

  const handleShow = (event: any) => {
    event.preventDefault()

    setShow(active => !active)
  }

  useEffect(() => {
    getCard()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    card,
    errors,
    show,
    handleShow,
    giftcardSettings,
  }
}
